import { Component, Mixins, Watch, Prop } from "vue-property-decorator";
import { mapState } from "vuex";
import _ from "@/boot/lodash";

// Mixins
import RequestPromises from "@/mixins/requestPromises";

// Enums/Data
import { DataModules } from "@/store/modules/data";

// Types
import { IState } from "@/store";
import { IFilter, IFiltersOptions } from "@/models/tables";
import { IProvisionCategory } from "@/models/provisioning";
import { OrgConfigKeys } from "@/data/constants";

@Component({
  computed: {
    ...mapState({
      categoriesScope(state: IState, getters) {
        return getters[
          `data/${DataModules.PROVISIONING_CATEGORIES}/categoriesScope`
        ];
      },
      categories(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.PROVISIONING_CATEGORIES,
          scope: this.categoriesScope
        });
      }
    })
  }
})
export default class ProvisioningMixin extends Mixins(RequestPromises) {
  // Data
  public categoriesScope!: string;
  public categories!: IProvisionCategory[];

  // Props

  @Prop({ default: null })
  public categoryCode!: IProvisionCategory["code"] | null;

  // Variables

  public provisionGuardFunctionalities = [
    "list_provision_blueprints",
    "list_provision_provider_configurations",
    "list_product_provision_configuration_attributes"
  ];

  public filters = [] as IFilter[];

  public getOrgProvisioning(ignoreStored = false) {
    return this.$store.dispatch("org/getConfig", {
      ignoreStored,
      keys: [OrgConfigKeys.PRODUCT_PROVISIONING_ENABLED]
    });
  }

  public getCategoriesWithProviders(ignoreStored = false) {
    return this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CATEGORIES}/list`,
      {
        scope: this.categoriesScope,
        params: {
          limit: 0,
          order: "order",
          with: "blueprints,providers,providers.logos"
        },
        vm: this,
        ignoreStored
      }
    );
  }

  public get categoriesWithProviders() {
    return _.map(
      _.filter(
        this.categories,
        category => _.get(category, "providers", []).length > 0
      ),
      category => {
        return {
          name: category.name,
          value: category.name,
          label: category.name,
          options: _.map(_.get(category, "providers", []), provider => {
            return {
              value: provider.name,
              label: provider.name
            };
          })
        };
      }
    );
  }

  public get selectedCategoryProviders() {
    return _.map(
      _.get(
        _.find(this.categories, ["code", this.categoryCode]),
        "providers",
        []
      ),
      provider => {
        return {
          value: provider.name,
          label: provider.name
        };
      }
    );
  }

  public get categoriesWithoutProviders() {
    return _.map(this.categories, category => {
      return {
        value: category.code,
        label: category.name
      };
    });
  }

  public get hasProvisioning() {
    return this.$store.getters["org/hasProvisioningEnabled"];
  }

  public get canShowProvisioning() {
    return (
      this.$userCan(...this.provisionGuardFunctionalities) &&
      this.hasProvisioning
    );
  }

  @Watch("categories", { deep: true })
  public onCategoriesChange() {
    this.filters = _.map(
      _.filter(this.filters, filter => {
        if (!this.categoryCode) return true;

        return filter.key !== "category.code";
      }),
      filter => {
        if (filter.key.indexOf("category.code") !== -1) {
          filter.options = this.categoriesWithoutProviders;

          if (this.categoriesWithoutProviders.length) {
            filter.value = this.categoriesWithoutProviders[0].value;
          }
        }

        if (filter.key.indexOf("provider.name") !== -1) {
          filter.options = (this.categoryCode
            ? this.selectedCategoryProviders
            : this.categoriesWithProviders) as IFiltersOptions[];

          if (filter.options.length) {
            filter.value = _.get(
              filter,
              `options[0]${this.categoryCode ? "" : ".options[0]"}.value`
            );
          }
        }
        return filter;
      }
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.PROVISIONING_CATEGORIES,
      scope: this.categoriesScope,
      vm: this
    });
  }
}
